import { downloadBlobAs, downloadText } from "@/features/preflight/lib/download";

import type { PersonaScriptDto } from "../api/persona-opportunity-scripts.api.js";
import {
  formatPersonaScriptDocument,
  formatPersonaScriptForCopy,
  type ScriptDocumentLabels,
} from "./persona-script-clipboard.js";
import { getPersonaScriptDisplayTitle } from "./script-display-title.js";

export const DOCX_MIME =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

/** Same break the plain-text export inserts between selected documents. */
export const DOCUMENT_EXPORT_SEPARATOR = "---";

export type PersonaDocumentExportFormat = "txt" | "docx";

export interface ScriptDocxLabels {
  sequence: string;
  concept: string;
  duration: string;
  presenter: string;
  background: string;
  product: string;
  scene: string;
  seeing: string;
  hearing: string;
  props: string;
  whyThisWorks: string;
}

/** Matches the clipboard dump, except rationale is labeled for production handoff. */
export const DEFAULT_SCRIPT_DOCX_LABELS: ScriptDocxLabels = {
  sequence: "Sequence",
  concept: "Concept",
  duration: "Duration",
  presenter: "Presenter",
  background: "Background",
  product: "Product",
  scene: "Scene",
  seeing: "Seeing",
  hearing: "Hearing",
  props: "Props",
  whyThisWorks: "Why this works",
};

export interface ScriptDocxPart {
  kind: "script";
  script: PersonaScriptDto;
  untitled: string;
  labels?: ScriptDocxLabels;
}

export interface BriefDocxPart {
  kind: "brief";
  text: string;
}

export type PersonaDocxPart = ScriptDocxPart | BriefDocxPart;

const FILENAME_UNSAFE = /[\\/:*?"<>|]/g;
const FILENAME_WHITESPACE = /\s+/g;
const FILENAME_BIDI_AND_CONTROL =
  /[\u0000-\u001F\u007F-\u009F\u200E\u200F\u202A-\u202E\u2066-\u2069\uFEFF\uFFFE\uFFFF]/g;
const FILENAME_TRAILING_DOTS = /\.+$/u;
const FILENAME_RESERVED = /^(con|prn|aux|nul|com[1-9]|lpt[1-9])$/i;
const MAX_SCRIPT_FILENAME_LENGTH = 80;

/** XML 1.0 illegal chars, plus the noncharacters Word rejects inside a text run. */
const DOCX_ILLEGAL_CHARS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\uFFFE\uFFFF]/g;
const RTL_CHARS = /[\u0590-\u08FF\uFB1D-\uFDFF\uFE70-\uFEFE]/;

export function scriptDocxLabels(
  t: (key: string, defaultValue: string) => string,
): ScriptDocxLabels {
  return {
    sequence: t("personaOpportunities.scripts.docx.sequence", "Sequence"),
    concept: t("personaOpportunities.scripts.docx.concept", "Concept"),
    duration: t("personaOpportunities.scripts.docx.duration", "Duration"),
    presenter: t("personaOpportunities.scripts.docx.presenter", "Presenter"),
    background: t("personaOpportunities.scripts.docx.background", "Background"),
    product: t("personaOpportunities.scripts.docx.product", "Product"),
    scene: t("personaOpportunities.scripts.docx.scene", "Scene"),
    seeing: t("personaOpportunities.scripts.docx.seeing", "Seeing"),
    hearing: t("personaOpportunities.scripts.docx.hearing", "Hearing"),
    props: t("personaOpportunities.scripts.docx.props", "Props"),
    whyThisWorks: t("personaOpportunities.scripts.docx.whyThisWorks", "Why this works"),
  };
}

function toDocumentLabels(labels: ScriptDocxLabels): ScriptDocumentLabels {
  return {
    sequence: labels.sequence,
    concept: labels.concept,
    duration: labels.duration,
    presenter: labels.presenter,
    background: labels.background,
    product: labels.product,
    scene: labels.scene,
    seeing: labels.seeing,
    hearing: labels.hearing,
    props: labels.props,
    rationale: labels.whyThisWorks,
  };
}

/**
 * Word body for one script. Same dump as copy and .txt; rationale is
 * "Why this works" because production readers do not know the product term.
 */
export function formatPersonaScriptForDocx(
  script: PersonaScriptDto,
  untitled: string,
  labels: ScriptDocxLabels = DEFAULT_SCRIPT_DOCX_LABELS,
): string {
  return formatPersonaScriptDocument(script, untitled, toDocumentLabels(labels));
}

/** Drops characters that make word/document.xml not well-formed, including lone surrogates. */
export function sanitizeDocxText(value: string): string {
  let cleaned = "";
  for (const char of value) {
    if (isLoneSurrogate(char)) continue;
    cleaned += char;
  }
  return cleaned.replace(DOCX_ILLEGAL_CHARS, "");
}

function isLoneSurrogate(char: string): boolean {
  if (char.length !== 1) return false;
  const code = char.charCodeAt(0);
  return code >= 0xd800 && code <= 0xdfff;
}

function lineNeedsRtl(value: string): boolean {
  return RTL_CHARS.test(value);
}

export function formatPersonaExportText(
  parts: readonly PersonaDocxPart[],
  format: PersonaDocumentExportFormat,
): string {
  return parts
    .map((part) =>
      part.kind === "brief"
        ? part.text
        : format === "docx"
          ? formatPersonaScriptForDocx(part.script, part.untitled, part.labels)
          : formatPersonaScriptForCopy(part.script, part.untitled),
    )
    .join(`\n\n${DOCUMENT_EXPORT_SEPARATOR}\n\n`);
}

export function personaSelectionFilename(
  scriptCount: number,
  briefCount: number,
  format: PersonaDocumentExportFormat,
): string {
  const stem =
    scriptCount > 0 && briefCount > 0
      ? "scripts-and-briefs-export"
      : briefCount > 0
        ? "briefs-export"
        : "scripts-export";
  return `${stem}.${format === "docx" ? "docx" : "txt"}`;
}

function downloadStem(title: string, fallback: string): string {
  const collapsed = title.replace(FILENAME_WHITESPACE, " ");
  const stripped = collapsed.replace(FILENAME_BIDI_AND_CONTROL, "").replace(FILENAME_UNSAFE, "");
  const sliced = Array.from(stripped).slice(0, MAX_SCRIPT_FILENAME_LENGTH).join("");
  const trimmed = sliced.trim().replace(FILENAME_TRAILING_DOTS, "").trim();
  if (trimmed.length === 0 || FILENAME_RESERVED.test(trimmed)) return fallback;
  return trimmed;
}

export function personaScriptDownloadFilename(title: string): string {
  return `${downloadStem(title, "script")}.docx`;
}

export async function buildPersonaDocumentsDocx(
  parts: readonly PersonaDocxPart[],
): Promise<Blob> {
  const { Document, Packer, Paragraph, TextRun } = await import("docx");
  const body = formatPersonaExportText(parts, "docx");
  const lines = body.length > 0 ? body.split("\n") : [""];
  const document = new Document({
    sections: [
      {
        children: lines.map((line) => {
          const text = sanitizeDocxText(line);
          if (text.length === 0) return new Paragraph({});
          if (!lineNeedsRtl(text)) return new Paragraph({ children: [new TextRun(text)] });
          return new Paragraph({
            bidirectional: true,
            children: [new TextRun({ text, rightToLeft: true })],
          });
        }),
      },
    ],
  });
  const base64 = await Packer.toBase64String(document);
  return base64ToBlob(base64);
}

export async function downloadPersonaScriptDocx(
  script: PersonaScriptDto,
  untitled: string,
  labels?: ScriptDocxLabels,
): Promise<void> {
  const blob = await buildPersonaDocumentsDocx([
    { kind: "script", script, untitled, labels },
  ]);
  downloadBlobAs(
    blob,
    personaScriptDownloadFilename(getPersonaScriptDisplayTitle(script, untitled)),
  );
}

export async function downloadPersonaSelection(
  parts: readonly PersonaDocxPart[],
  format: PersonaDocumentExportFormat,
  filename: string,
): Promise<void> {
  if (format === "txt") {
    downloadText(formatPersonaExportText(parts, "txt"), filename, "text/plain;charset=utf-8");
    return;
  }
  const blob = await buildPersonaDocumentsDocx(parts);
  downloadBlobAs(blob, filename);
}

function base64ToBlob(base64: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Blob([bytes], { type: DOCX_MIME });
}
